import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import "./DevDashboard.css";
import DevKpiCards from "./DevKpiCards";
import DevAnalyticsPanels from "./DevAnalyticsPanels";
import DevPerformanceSection from "./DevPerformanceSection";
import DevMachinePortfolioTable from "./DevMachinePortfolioTable";
import DevSidebarInfo from "./DevSidebarInfo";
import {
  DevKpiCardsSkeleton,
  DevPerformanceSkeleton,
  DevPortfolioTableSkeleton,
} from "./DevDashboardSkeleton";
import { apiCall } from "../../services/AxiosService";
import { API_URL } from "../../services/APIRoute";

const PALETTE = [
  "#3b82f6",
  "#a855f7",
  "#10b981",
  "#f59e0b",
  "#38bdf8",
  "#ec4899",
  "#8b5cf6",
];

const extractData = (res) => {
  if (!res) return null;
  if (res.data && typeof res.data === "object" && "data" in res.data) {
    return res.data.data;
  }
  if (res.data !== undefined) {
    return res.data;
  }
  return res;
};

const getDateRangeForDays = (daysStr) => {
  const days = parseInt(daysStr, 10) || 30;
  const end = moment();
  const start = moment().subtract(days - 1, "days");
  return {
    startDate: start.format("YYYY-MM-DD"),
    endDate: end.format("YYYY-MM-DD"),
    period: `Last ${days} Days`,
  };
};

const getVal = (v) => {
  if (v == null) return null;
  if (Array.isArray(v)) return v.length > 0 ? v[0] : null;
  return v;
};

// KPI card templates with zero/empty values — structure and styling preserved
const emptyKpiCards = [
  {
    id: "total-machines",
    title: "Total Machines",
    value: "0",
    iconType: "cube",
    iconColor: "#2563eb",
    iconBg: "#eff6ff",
  },
  {
    id: "active-machines",
    title: "Active Machines",
    value: "0",
    subtext: "",
    iconType: "cube",
    iconColor: "#16a34a",
    iconBg: "#ecfdf5",
  },
  {
    id: "total-workflows",
    title: "Workflows Using Machines",
    value: "0",
    iconType: "workflow",
    iconColor: "#7c3aed",
    iconBg: "#f5f3ff",
  },
  {
    id: "total-runs",
    title: "Total Runs",
    value: "0",
    iconType: "play",
    iconColor: "#2563eb",
    iconBg: "#eff6ff",
  },
  {
    id: "success-rate",
    title: "Success Rate",
    value: "0.0%",
    iconType: "checkCircle",
    iconColor: "#16a34a",
    iconBg: "#ecfdf5",
  },
  {
    id: "total-revenue",
    title: "Total Earnings",
    value: "$0.00",
    iconType: "revenue",
    iconColor: "#7c3aed",
    iconBg: "#f5f3ff",
  },
  {
    id: "average-rating",
    title: "Average Rating",
    value: "0.0",
    ratingCount: "0",
    iconType: "star",
    iconColor: "#d97706",
    iconBg: "#fffbeb",
  },
  {
    id: "last-activity",
    title: "Last Activity",
    value: "-",
    iconType: "clock",
    iconColor: "#2563eb",
    iconBg: "#eff6ff",
  },
];

// Empty chart data structure
const emptyChartsData = {
  usage: {
    period: "30",
    periodLabel: "Last 30 Days",
    daysLabels: [],
    featuredTicks: [],
    runs: [],
  },
  revenue: {
    period: "30",
    periodLabel: "Last 30 Days",
    daysLabels: [],
    featuredTicks: [],
    values: [],
  },
  workflow: {
    period: "30",
    periodLabel: "Last 30 Days",
    items: [],
  },
  status: {
    period: "30",
    periodLabel: "Last 30 Days",
    daysLabels: [],
    featuredTicks: [],
    successful: [],
    failed: [],
  },
};

// Empty performance cards — structure preserved, zero values
const emptyPerformanceData = [
  {
    id: "top-performing",
    iconType: "trophy",
    iconColor: "#d97706",
    iconBg: "#fef3c7",
    label: "Top Performing Machine",
    title: "-",
    metrics: "Success Rate: 0.0% | Runs: 0",
  },
  {
    id: "most-used",
    iconType: "chart",
    iconColor: "#0284c7",
    iconBg: "#e0f2fe",
    label: "Most Used Machine",
    title: "-",
    metrics: "Runs: 0 | Workflows: 0",
  },
  {
    id: "highest-earning",
    iconType: "dollar",
    iconColor: "#16a34a",
    iconBg: "#dcfce7",
    label: "Highest Earning Machine",
    title: "-",
    metrics: "Earnings: $0.00 | Runs: 0",
  },
];

// Empty developer info
const emptyDevInfo = {
  status: "-",
  memberSince: "-",
  lastUpdated: "-",
  email: "-",
  totalMachines: 0,
  publishedMachines: 0,
  draftMachines: 0,
  activeWorkflows: 0,
  totalRunsAllTime: "0",
  totalEarnings: "$0.00",
  averageRating: "0.0",
  ratingCount: 0,
};

// Empty support info
const emptySupportInfo = {
  email: "-",
  supportHours: "-",
  responseTime: "-",
  regardsText: "",
};

const mapKpiCards = (k, base) => {
  if (!k) return base;
  return base.map((card) => {
    switch (card.id) {
      case "total-machines":
        return {
          ...card,
          value: k.totalmachines != null ? String(k.totalmachines) : card.value,
        };
      case "active-machines":
        return {
          ...card,
          value: k.activemachines != null ? String(k.activemachines) : card.value,
          subtext:
            k.totalmachines && Number(k.totalmachines) > 0 && k.activemachines != null
              ? `${Math.round(
                ((Number(k.activemachines) || 0) / Number(k.totalmachines)) * 100
              )}% of total`
              : card.subtext,
        };
      case "total-workflows":
        return {
          ...card,
          value: k.totalworkflows != null ? String(k.totalworkflows) : card.value,
        };
      case "total-runs":
        return {
          ...card,
          value:
            k.totalruns != null
              ? Number(k.totalruns).toLocaleString()
              : card.value,
        };
      case "success-rate":
        return {
          ...card,
          value:
            k.successrate != null
              ? `${Number(k.successrate).toFixed(1)}%`
              : card.value,
        };
      case "total-revenue":
        return {
          ...card,
          value:
            k.totalearnings != null
              ? `$${Number(k.totalearnings).toFixed(2)}`
              : card.value,
        };
      case "average-rating":
        return {
          ...card,
          value:
            k.averagerating != null
              ? Number(k.averagerating).toFixed(1)
              : card.value,
          ratingCount:
            k.ratingcount != null ? String(k.ratingcount) : card.ratingCount,
        };
      case "last-activity":
        return {
          ...card,
          value: k.lastactivity
            ? moment(k.lastactivity).format("MMM DD, YYYY")
            : card.value,
        };
      default:
        return card;
    }
  });
};

const computeDaysAndTicks = (trendArray) => {
  if (!Array.isArray(trendArray) || trendArray.length === 0) {
    return {
      daysLabels: [],
      featuredTicks: [],
    };
  }
  const daysLabels = trendArray.map(
    (item) => item.day_label || moment(item.full_date || item.starton).format("DD MMM")
  );

  const total = daysLabels.length;
  let featuredTicks = [];

  if (total <= 7) {
    // 7 days or fewer: display every day
    featuredTicks = [...daysLabels];
  } else if (total >= 28 && total <= 31) {
    // 30-day view: show clean 5-day intervals across the month (0, 5, 10, 15, 20, 25, and last day)
    const indices = [0, 5, 10, 15, 20, 25, total - 1];
    featuredTicks = indices.map((idx) => daysLabels[idx]).filter(Boolean);
  } else {
    // For 90 days or any other range: evenly distribute ~7 ticks from start to end
    const targetCount = 7;
    const step = (total - 1) / (targetCount - 1);
    const indices = [];
    for (let i = 0; i < targetCount; i++) {
      indices.push(Math.min(total - 1, Math.round(i * step)));
    }
    const uniqueIndices = [...new Set(indices)];
    featuredTicks = uniqueIndices.map((idx) => daysLabels[idx]).filter(Boolean);
  }

  return { daysLabels, featuredTicks };
};

const mapPerformance = (p, base) => {
  if (!p) return base;
  return base.map((card) => {
    switch (card.id) {
      case "top-performing": {
        const name = getVal(p.top_performing?.machinename);
        if (name) {
          const rateVal = getVal(p.top_performing?.success_rate);
          const runsVal = getVal(p.top_performing?.runs);
          const rate = rateVal != null ? `${Number(rateVal).toFixed(1)}%` : "0.0%";
          const runs = runsVal != null ? Number(runsVal).toLocaleString() : "0";
          return {
            ...card,
            title: name,
            metrics: `Success Rate: ${rate} | Runs: ${runs}`,
          };
        }
        return card;
      }
      case "most-used": {
        const name = getVal(p.most_reliable?.machinename);
        if (name) {
          const runsVal = getVal(p.most_reliable?.runs);
          const wfVal = getVal(p.most_reliable?.workflows);
          const rateVal = getVal(p.most_reliable?.success_rate);
          const runs = runsVal != null ? Number(runsVal).toLocaleString() : "0";
          const wf =
            wfVal != null
              ? `Workflows: ${wfVal}`
              : rateVal != null
                ? `Success Rate: ${Number(rateVal).toFixed(1)}%`
                : "Workflows: 0";
          return {
            ...card,
            title: name,
            metrics: `Runs: ${runs} | ${wf}`,
          };
        }
        return card;
      }
      case "highest-earning": {
        const name = getVal(p.highest_revenue?.machinename);
        if (name) {
          const revVal = getVal(p.highest_revenue?.revenue);
          const runsVal = getVal(p.highest_revenue?.runs);
          const earn = revVal != null ? `$${Number(revVal).toFixed(2)}` : "$0.00";
          const runs = runsVal != null ? Number(runsVal).toLocaleString() : "0";
          return {
            ...card,
            title: name,
            metrics: `Earnings: ${earn} | Runs: ${runs}`,
          };
        }
        return card;
      }
      default:
        return card;
    }
  });
};

const mapPortfolio = (list, base) => {
  if (!Array.isArray(list) || list.length === 0) return base;
  return list.map((item, index) => ({
    id: index + 1,
    machineId: item.machineid,
    refmachineid:
      item.refmachineid != null ? Number(item.refmachineid) : 0,
    refMachineId:
      item.refmachineid != null ? Number(item.refmachineid) : 0,
    machineName: item.machinename || item.machineName || "-",
    industry: item.industryname || item.industry || "-",
    category: item.categoryname || item.category || "-",
    createdOn: item.createdon ? moment(item.createdon).format("MM/DD/YYYY") : "-",
    version: item.versionnumber || item.version || "1.0.0",
    versioncount:
      item.versioncount != null
        ? Number(item.versioncount)
        : item.versionCount != null
          ? Number(item.versionCount)
          : 1,
    versionCount:
      item.versioncount != null
        ? Number(item.versioncount)
        : item.versionCount != null
          ? Number(item.versionCount)
          : 1,
    status: item.status === "Publish" ? "Active" : (item.status || "Active"),
    workflows: item.workflows != null ? Number(item.workflows) : 0,
    totalRuns: item.totalruns != null ? Number(item.totalruns) : 0,
    machinesuccesscount:
      item.machinesuccesscount != null
        ? Number(item.machinesuccesscount)
        : item.machineSuccessCount != null
          ? Number(item.machineSuccessCount)
          : 0,
    machinefailedcount:
      item.machinefailedcount != null
        ? Number(item.machinefailedcount)
        : item.machineFailedCount != null
          ? Number(item.machineFailedCount)
          : 0,
    successCount:
      item.machinesuccesscount != null
        ? Number(item.machinesuccesscount)
        : item.machineSuccessCount != null
          ? Number(item.machineSuccessCount)
          : 0,
    failedCount:
      item.machinefailedcount != null
        ? Number(item.machinefailedcount)
        : item.machineFailedCount != null
          ? Number(item.machineFailedCount)
          : 0,
    totalusagetime: item.totalusagetime || item.totalUsageTime || "00:00 min",
    usages: item.totalusagetime || item.totalUsageTime || "00:00 min",
    earnings:
      item.earnings != null
        ? String(item.earnings).startsWith("$")
          ? item.earnings
          : `$${Number(item.earnings).toFixed(0)}`
        : "$0",
    rating: item.rating != null ? Number(item.rating) : 0,
    ratingCount: item.ratingcount != null ? Number(item.ratingcount) : 0,
    lastUsed: item.lastrun
      ? moment(item.lastrun).format("MMM DD, YYYY")
      : item.createdon
        ? moment(item.createdon).format("MMM DD, YYYY")
        : "-",
    rawItem: item,
  }));
};

const mapDevInfo = (info, k, base) => {
  if (!info && !k) return base;
  return {
    status: info?.workstatus || base.status,
    memberSince: info?.createdon
      ? moment(info.createdon).format("MMMM YYYY")
      : base.memberSince,
    lastUpdated: info?.updatedon
      ? moment(info.updatedon).format("MMM DD, YYYY")
      : k?.lastactivity
        ? moment(k.lastactivity).format("MMM DD, YYYY")
        : base.lastUpdated,
    email: info?.email || base.email,
    totalMachines:
      k?.totalmachines != null ? k.totalmachines : base.totalMachines,
    publishedMachines:
      k?.activemachines != null ? k.activemachines : base.publishedMachines,
    draftMachines:
      k?.totalmachines != null && k?.activemachines != null
        ? Math.max(k.totalmachines - k.activemachines, 0)
        : base.draftMachines,
    activeWorkflows:
      k?.totalworkflows != null ? k.totalworkflows : base.activeWorkflows,
    totalRunsAllTime:
      k?.totalruns != null
        ? Number(k.totalruns).toLocaleString()
        : base.totalRunsAllTime,
    totalEarnings:
      k?.totalearnings != null
        ? `$${Number(k.totalearnings).toFixed(2)}`
        : base.totalEarnings,
    averageRating:
      k?.averagerating != null
        ? Number(k.averagerating).toFixed(1)
        : base.averageRating,
    ratingCount: k?.ratingcount != null ? k.ratingcount : base.ratingCount,
  };
};

const mapSupport = (supp, info, base) => {
  if (!supp && !info) return base;
  return {
    email: supp?.supportemail || supp?.email || info?.email || base.email,
    supportHours: supp?.supporthours || base.supportHours,
    responseTime: supp?.responsetime || base.responseTime,
    regardsText: supp?.regardstext || base.regardsText,
  };
};

/**
 * Developer Dashboard Page (/dashboard)
 * Connected to quantum-dev-api Dev Dashboard API.
 * Shows empty/zero state when API fails — no fake mock data.
 * Strictly preserves the pristine design, colors, cards, and layouts.
 */
const DevDashboard = () => {
  const [searchParams] = useSearchParams();
  const userInfo = useSelector((state) => state?.userinfo?.UserInfo);

  const paramId = searchParams.get("id") || searchParams.get("subscriberid");
  const effectiveSubscriberId =
    Number(paramId) ||
    Number(userInfo?.subscriberid) ||
    Number(userInfo?.id) ||
    83;

  // Individual loading states — each section renders as soon as its API completes
  const [isKpisLoading, setIsKpisLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState({
    usage: true,
    revenue: true,
    workflow: true,
    status: true,
  });
  const [isPerfLoading, setIsPerfLoading] = useState(true);
  const [isPortfolioLoading, setIsPortfolioLoading] = useState(true);
  const [sidebarLoading, setSidebarLoading] = useState({
    info: true,
    skills: true,
    support: true,
  });

  // State handles initialized with empty/zero data — only API-returned data will populate these
  const [kpiData, setKpiData] = useState(emptyKpiCards);
  const [chartsData, setChartsData] = useState(emptyChartsData);
  const [perfData, setPerfData] = useState(emptyPerformanceData);
  const [portfolioData, setPortfolioData] = useState([]);
  const [devInfo, setDevInfo] = useState(emptyDevInfo);
  const [skills, setSkills] = useState([]);
  const [supportInfo, setSupportInfo] = useState(emptySupportInfo);

  // Height synchronization refs to keep Machine Portfolio and Sidebar flush-aligned
  const sidebarWrapperRef = useRef(null);
  const portfolioWrapperRef = useRef(null);
  const [portfolioHeight, setPortfolioHeight] = useState(null);

  useEffect(() => {
    const syncHeight = () => {
      if (typeof window === "undefined") return;
      if (window.innerWidth < 1200) {
        setPortfolioHeight(null);
        return;
      }

      // Find the Support & Notes card (last card in sidebar)
      const supportCard =
        document.querySelector(".dev-support-notes-card") ||
        sidebarWrapperRef.current?.querySelector(".dev-sidebar-card:last-child") ||
        sidebarWrapperRef.current?.querySelector(".dev-sidebar > div:last-child");

      // Find the Machine Portfolio card
      const portfolioCard =
        portfolioWrapperRef.current?.querySelector(".dev-portfolio-card") ||
        portfolioWrapperRef.current;

      if (supportCard && portfolioCard) {
        const supportRect = supportCard.getBoundingClientRect();
        const portfolioRect = portfolioCard.getBoundingClientRect();

        // Exact height from the top of the portfolio card to the bottom of Support & Notes
        const exactDiff = Math.round(supportRect.bottom - portfolioRect.top);

        if (exactDiff > 120) {
          setPortfolioHeight((prev) => {
            if (prev === exactDiff) return prev;
            return exactDiff;
          });
        }
      }
    };

    // Staggered triggers as layout stabilizes and APIs return
    syncHeight();
    const rafId = requestAnimationFrame(syncHeight);
    const t1 = setTimeout(syncHeight, 60);
    const t2 = setTimeout(syncHeight, 180);
    const t3 = setTimeout(syncHeight, 350);
    const t4 = setTimeout(syncHeight, 700);
    const t5 = setTimeout(syncHeight, 1200);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        syncHeight();
      });
      if (sidebarWrapperRef.current) resizeObserver.observe(sidebarWrapperRef.current);
      const supportEl = document.querySelector(".dev-support-notes-card");
      if (supportEl) resizeObserver.observe(supportEl);
      if (portfolioWrapperRef.current) resizeObserver.observe(portfolioWrapperRef.current);
    }

    let mutationObserver;
    if (typeof MutationObserver !== "undefined" && sidebarWrapperRef.current) {
      mutationObserver = new MutationObserver(() => {
        syncHeight();
      });
      mutationObserver.observe(sidebarWrapperRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    window.addEventListener("resize", syncHeight);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
      window.removeEventListener("resize", syncHeight);
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [
    portfolioData,
    isPortfolioLoading,
    sidebarLoading,
    devInfo,
    supportInfo,
    skills,
    isPerfLoading,
    chartLoading,
  ]);

  // 1. API: Developer KPIs
  const fetchKpis = useCallback(async () => {
    setIsKpisLoading(true);
    try {
      const res = await apiCall(
        {
          method: "POST",
          url: API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_KPIS,
          body: { subscriberid: effectiveSubscriberId },
        },
        false
      );
      const data = extractData(res);
      if (data && typeof data === "object") {
        setKpiData((prev) => mapKpiCards(data, prev));
        setDevInfo((prev) => mapDevInfo(null, data, prev));
      }
    } catch (err) {
      console.error("Error fetching KPIs:", err);
    } finally {
      setIsKpisLoading(false);
    }
  }, [effectiveSubscriberId]);

  // 2. API: Usage, Revenue, and Run Status Trends (Initial: Last 30 Days)
  const fetchTrends = useCallback(async () => {
    setChartLoading((prev) => ({
      ...prev,
      usage: true,
      revenue: true,
      status: true,
    }));
    try {
      const { startDate, endDate, period } = getDateRangeForDays("30");
      const res = await apiCall(
        {
          method: "POST",
          url: API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_USAGE_REVENUE_TREND,
          body: {
            subscriberid: effectiveSubscriberId,
            startdate: startDate,
            enddate: endDate,
            period,
          },
        },
        false
      );
      const data = extractData(res);
      if (Array.isArray(data) && data.length > 0) {
        const { daysLabels, featuredTicks } = computeDaysAndTicks(data);
        const runs = data.map((i) => Number(i.runs || 0));
        const revenue = data.map((i) => Number(i.revenue || 0));
        const successful = data.map((i) => Number(i.successful || 0));
        const failed = data.map((i) => Number(i.failed || 0));

        setChartsData((prev) => ({
          ...prev,
          usage: {
            ...prev.usage,
            daysLabels,
            featuredTicks,
            runs,
          },
          revenue: {
            ...prev.revenue,
            daysLabels,
            featuredTicks,
            values: revenue,
          },
          status: {
            ...prev.status,
            daysLabels,
            featuredTicks,
            successful,
            failed,
          },
        }));
      }
    } catch (err) {
      console.error("Error fetching usage/revenue trends:", err);
    } finally {
      setChartLoading((prev) => ({
        ...prev,
        usage: false,
        revenue: false,
        status: false,
      }));
    }
  }, [effectiveSubscriberId]);

  // 3. API: Workflow Usage by Machine (Initial: Last 30 Days)
  const fetchWorkflowUsage = useCallback(async () => {
    setChartLoading((prev) => ({ ...prev, workflow: true }));
    try {
      const { startDate, endDate, period } = getDateRangeForDays("30");
      const res = await apiCall(
        {
          method: "POST",
          url: API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_WORKFLOW_USAGE,
          body: {
            subscriberid: effectiveSubscriberId,
            startdate: startDate,
            enddate: endDate,
            period,
            limit: 5,
          },
        },
        false
      );
      const data = extractData(res);
      if (Array.isArray(data) && data.length > 0) {
        const mappedWf = data
          .map((item, idx) => ({
            machineName: item.machinename || item.machineName || "-",
            count: Number(item.runs || item.count || 0),
            color: item.color || PALETTE[idx % PALETTE.length],
          }))
          .filter((item) => item.count > 0);
        setChartsData((prev) => ({
          ...prev,
          workflow: {
            ...prev.workflow,
            items: mappedWf,
          },
        }));
      }
    } catch (err) {
      console.error("Error fetching workflow usage:", err);
    } finally {
      setChartLoading((prev) => ({ ...prev, workflow: false }));
    }
  }, [effectiveSubscriberId]);

  // 4. API: Developer Performance
  const fetchPerformance = useCallback(async () => {
    setIsPerfLoading(true);
    try {
      const res = await apiCall(
        {
          method: "POST",
          url: API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_PERFORMANCE,
          body: { subscriberid: effectiveSubscriberId },
        },
        false
      );
      const data = extractData(res);
      if (data && typeof data === "object") {
        setPerfData((prev) => mapPerformance(data, prev));
      }
    } catch (err) {
      console.error("Error fetching performance:", err);
    } finally {
      setIsPerfLoading(false);
    }
  }, [effectiveSubscriberId]);

  // 5. API: Machine Portfolio Table List
  const fetchPortfolio = useCallback(async () => {
    setIsPortfolioLoading(true);
    try {
      const res = await apiCall(
        {
          method: "POST",
          url: API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_PORTFOLIO_LIST,
          body: {
            subscriberid: effectiveSubscriberId,
            pagesize: 100,
            pagenumber: 1,
          },
        },
        false
      );
      const data = extractData(res);
      if (Array.isArray(data) && data.length > 0) {
        setPortfolioData((prev) => mapPortfolio(data, prev));
      }
    } catch (err) {
      console.error("Error fetching portfolio:", err);
    } finally {
      setIsPortfolioLoading(false);
    }
  }, [effectiveSubscriberId]);

  // 6. API: Skills & Categories
  const fetchSkills = useCallback(async () => {
    setSidebarLoading((prev) => ({ ...prev, skills: true }));
    try {
      const res = await apiCall(
        {
          method: "POST",
          url: API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_SKILLS_CATEGORIES,
          body: { subscriberid: effectiveSubscriberId },
        },
        false
      );
      const data = extractData(res);
      if (Array.isArray(data) && data.length > 0) {
        setSkills(data);
      }
    } catch (err) {
      console.error("Error fetching skills:", err);
    } finally {
      setSidebarLoading((prev) => ({ ...prev, skills: false }));
    }
  }, [effectiveSubscriberId]);

  // 7. API: Support Notes & Developer Info
  const fetchSupportNotes = useCallback(async () => {
    setSidebarLoading((prev) => ({ ...prev, support: true, info: true }));
    try {
      const res = await apiCall(
        {
          method: "POST",
          url: API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_SUPPORT_NOTES,
          body: { subscriberid: effectiveSubscriberId },
        },
        false
      );
      const data = extractData(res);
      if (data && typeof data === "object") {
        setSupportInfo((prev) => mapSupport(data, null, prev));
        setDevInfo((prev) => mapDevInfo(data, null, prev));
      }
    } catch (err) {
      console.error("Error fetching support notes:", err);
    } finally {
      setSidebarLoading((prev) => ({ ...prev, support: false, info: false }));
    }
  }, [effectiveSubscriberId]);

  // Trigger all independent API fetches concurrently on mount/ID change
  useEffect(() => {
    if (!effectiveSubscriberId) return;
    fetchKpis();
    fetchTrends();
    fetchWorkflowUsage();
    fetchPerformance();
    fetchPortfolio();
    fetchSkills();
    fetchSupportNotes();
  }, [
    effectiveSubscriberId,
    fetchKpis,
    fetchTrends,
    fetchWorkflowUsage,
    fetchPerformance,
    fetchPortfolio,
    fetchSkills,
    fetchSupportNotes,
  ]);

  // Handle period change from individual chart dropdowns (30, 7, 90 Days)
  // Each chart updates strictly its own dataset without interfering with others
  const handlePeriodChange = useCallback(
    async (chartType, days) => {
      const { startDate, endDate, period } = getDateRangeForDays(days);
      const payload = {
        subscriberid: effectiveSubscriberId,
        startdate: startDate,
        enddate: endDate,
        period,
      };

      setChartLoading((prev) => ({ ...prev, [chartType]: true }));
      try {
        if (chartType === "usage") {
          const res = await apiCall(
            {
              method: "POST",
              url:
                API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_USAGE_REVENUE_TREND,
              body: payload,
            },
            false
          );
          const raw = extractData(res);
          if (Array.isArray(raw) && raw.length > 0) {
            const { daysLabels, featuredTicks } = computeDaysAndTicks(raw);
            const runs = raw.map((i) => Number(i.runs || 0));
            setChartsData((prev) => ({
              ...prev,
              usage: {
                period: String(days),
                periodLabel: period,
                daysLabels,
                featuredTicks,
                runs,
              },
            }));
          }
        } else if (chartType === "revenue") {
          const res = await apiCall(
            {
              method: "POST",
              url:
                API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_USAGE_REVENUE_TREND,
              body: payload,
            },
            false
          );
          const raw = extractData(res);
          if (Array.isArray(raw) && raw.length > 0) {
            const { daysLabels, featuredTicks } = computeDaysAndTicks(raw);
            const values = raw.map((i) => Number(i.revenue || 0));
            setChartsData((prev) => ({
              ...prev,
              revenue: {
                period: String(days),
                periodLabel: period,
                daysLabels,
                featuredTicks,
                values,
              },
            }));
          }
        } else if (chartType === "workflow") {
          const res = await apiCall(
            {
              method: "POST",
              url: API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_WORKFLOW_USAGE,
              body: { ...payload, limit: 5 },
            },
            false
          );
          const raw = extractData(res);
          if (Array.isArray(raw) && raw.length > 0) {
            const mappedWf = raw
              .map((item, idx) => ({
                machineName: item.machinename || item.machineName || "-",
                count: Number(item.runs || item.count || 0),
                color: item.color || PALETTE[idx % PALETTE.length],
              }))
              .filter((item) => item.count > 0);
            setChartsData((prev) => ({
              ...prev,
              workflow: {
                period: String(days),
                periodLabel: period,
                items: mappedWf,
              },
            }));
          }
        } else if (chartType === "status") {
          const res = await apiCall(
            {
              method: "POST",
              url:
                API_URL.BASEURL + API_URL.SUBSCRIBERS_DEV_USAGE_REVENUE_TREND,
              body: payload,
            },
            false
          );
          const raw = extractData(res);
          if (Array.isArray(raw) && raw.length > 0) {
            const { daysLabels, featuredTicks } = computeDaysAndTicks(raw);
            const successful = raw.map((i) => Number(i.successful || 0));
            const failed = raw.map((i) => Number(i.failed || 0));
            setChartsData((prev) => ({
              ...prev,
              status: {
                period: String(days),
                periodLabel: period,
                daysLabels,
                featuredTicks,
                successful,
                failed,
              },
            }));
          }
        }
      } catch (err) {
        console.error(`Error updating period for ${chartType}:`, err);
      } finally {
        setChartLoading((prev) => ({ ...prev, [chartType]: false }));
      }
    },
    [effectiveSubscriberId]
  );

  return (
    <div className="dashboard-main market-place-page-main main-dev-dahsboard dev-dashboard-root">
      {/* 1. Eight KPI Cards Across the Top */}
      {isKpisLoading ? (
        <DevKpiCardsSkeleton />
      ) : (
        <DevKpiCards cards={kpiData} />
      )}

      {/* 2. Main Content Grid: Analytics & Table (Left) + Information Sidebar (Right) */}
      <div className="dev-main-grid">
        {/* Main Column */}
        <div className="dev-left-column">
          {/* Four Analytics Panels */}
          <div className="dev-analytics-wrapper">
            <DevAnalyticsPanels
              data={chartsData}
              onPeriodChange={handlePeriodChange}
              loading={chartLoading}
            />
          </div>

          {/* Developer Performance (Last 30 Days) */}
          <div className="dev-perf-wrapper">
            {isPerfLoading ? (
              <DevPerformanceSkeleton />
            ) : (
              <DevPerformanceSection items={perfData} />
            )}
          </div>

          {/* Machine Portfolio Table */}
          <div ref={portfolioWrapperRef} className="dev-portfolio-wrapper" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {isPortfolioLoading ? (
              <DevPortfolioTableSkeleton tableHeight={portfolioHeight} />
            ) : (
              <DevMachinePortfolioTable
                initialRows={portfolioData}
                tableHeight={portfolioHeight}
              />
            )}
          </div>
        </div>

        {/* Right Sidebar: Developer Info, Skills & Categories, Support & Notes */}
        <div ref={sidebarWrapperRef} className="dev-sidebar-wrapper">
          <DevSidebarInfo
            developerInfo={devInfo}
            skills={skills}
            support={supportInfo}
            loading={sidebarLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default DevDashboard;
